import { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Spinner, TooltipProvider } from '@monksflow/monks-ui';
import { ChevronRight, PackageIcon } from 'lucide-react';
import { CONNECTOR_PLATFORM_LABELS, type ConnectorPlatform } from '@/entities/media-connector';
import {
  deliveryJobQueryOptions,
  jobPlanQueryOptions,
  usePlatformSchemas,
  useBindJobAccount,
  useRemoveJobPlatform,
  useBindJobPage,
  useBindJobAdvertiser,
  useCheckDeliveryStatus,
  usePublishJob,
  useFetchPublishReport,
  useDryRunJob,
  MEDIA_CAMPAIGN_QUERY_KEYS,
  useEditNodeField,
  useRunPreflight,
  useClearPreflight,
  useCreativesUploading,
  useDismissPendingSource,
  useReadSourceTabs,
  useUploadJobSource,
  type DeliveryJob,
  type JobPublishReport,
  type PreflightResult,
} from '@/entities/media-campaign';
import { TabPickerDialog } from '@/features/delivery-job-create';
import {
  JobHistoryPanel,
  JobPlatformPanel,
  PublishActions,
  PublishConfirmDialog,
  PublishReportDialog,
  afterEdit,
  publishGate,
  showsIssues,
  statusFromJob,
  type PreflightStatus,
} from '@/features/delivery-job-review';
import type { ProjectDetailOutletContext } from '@/pages/ProjectDetail/projectDetail.types';
import { PageTransition } from '@/shared/ui/animations';
import { ListPageLayout } from '@/shared/ui/layout';
import { EmptyState } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import { JobPlatformTabs } from './components/JobPlatformTabs';
import { notificationService } from '@/lib/services/notification.service';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';

/**
 * A platform key as a person would read it, falling back to the key.
 *
 * Indexed through a partial view of the label table on purpose: the wire value is a string,
 * and a platform added on the server before this app knows about it would otherwise render
 * as `undefined` inside a sentence.
 */
function platformLabel(platform: string): string {
  const labels: Partial<Record<string, string>> = CONNECTOR_PLATFORM_LABELS;
  return labels[platform] ?? platform;
}

/** What a check found, for the confirmation that it ran at all. */
function summarisePreflight(result: PreflightResult): string {
  if (result.errors === 0 && result.warnings === 0)
    return i18n.t('projects:projectDetail.jobReview.preflightFoundNothing');
  const parts = [
    result.errors > 0
      ? i18n.t('projects:projectDetail.jobReview.toFix', { count: result.errors })
      : null,
    result.warnings > 0
      ? i18n.t('projects:projectDetail.jobReview.toCheck', { count: result.warnings })
      : null,
  ].filter(Boolean);
  return i18n.t('projects:projectDetail.jobReview.preflightFound', {
    parts: parts.join(i18n.t('projects:projectDetail.jobReview.listJoin')),
  });
}

/** Stands in for a job never checked, so the first run has something to differ from. */
// eslint-disable-next-line i18next/no-literal-string -- sentinel compared against a timestamp
const NEVER_RUN = 'never';

/**
 * How long to wait for a queued platform check before giving up on it. Generous
 * because the work is a real call out to each platform, but bounded because a
 * worker that throws never records the run at all.
 */
const DRY_RUN_WAIT_LIMIT_MS = 120_000;

/**
 * Project Detail → Delivery → one Job, reviewed before it is published.
 *
 * One platform per tab, because the hierarchy, the field names and the
 * validation all change at that boundary — and because a single table per tab
 * can own the page's scroll, which a stack of them cannot. Publish stays global:
 * the whole plan goes out together.
 */
/**
 * What to do about an account a check cannot run through, which differs by how it broke.
 *
 * An account with no advertiser is unfinished rather than broken: "reconnect it" sends
 * the reviewer to redo the one step that would not fix it. Where we tried to sign in and
 * were told why, the platform's own words are shown instead of our status word — "is
 * error" names a column of ours, not the problem.
 */
function describeUnusableAccount(account: {
  name: string;
  status?: string;
  reason: 'unreachable' | 'no_advertiser';
  detail?: string;
}): string {
  if (account.reason === 'no_advertiser') {
    return i18n.t('projects:projectDetail.jobReview.noAdvertiser', { name: account.name });
  }
  if (account.detail) {
    return i18n.t('projects:projectDetail.jobReview.couldNotSignIn', {
      name: account.name,
      detail: account.detail,
    });
  }
  return i18n.t('projects:projectDetail.jobReview.accountUnreachable', {
    name: account.name,
    status: account.status ?? i18n.t('projects:projectDetail.jobReview.notReachable'),
  });
}

export function JobReview() {
  const { t } = useTranslation('projects');
  const { project } = useOutletContext<ProjectDetailOutletContext>();
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const projectId = project.platformProjectId;

  const [report, setReport] = useState<JobPublishReport | null>(null);
  const [platform, setPlatform] = useState<ConnectorPlatform | null>(null);
  const [localPreflight, setLocalPreflight] = useState<PreflightStatus | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  // A kept client workbook whose tabs still have to be picked; the picker opens from here
  // too, so a reload or a shared link mid-choice does not lose the question.
  const [isTabPickerOpen, setIsTabPickerOpen] = useState(false);
  const readTabs = useReadSourceTabs(projectId);
  const dismissPending = useDismissPendingSource(projectId);
  // A plan uploaded from this page, after a read that gave up: the page's own states take
  // over from the answer (the grid, the reading banner, or the tab choice).
  const uploadSource = useUploadJobSource(projectId);
  const uploadInput = useRef<HTMLInputElement | null>(null);
  const uploadPlan = (file: File) => {
    if (!job) return;
    uploadSource.mutate(
      { job, file },
      {
        onSuccess: ({ ingested }) => void ingested?.then(announceRead),
        onError: (error) =>
          notificationService.showError(
            error instanceof Error
              ? error.message
              : t('projectDetail.jobReview.planCouldNotBeRead'),
          ),
      },
    );
  };
  const announceRead = (read: DeliveryJob) => {
    if (read.sourceError) notificationService.showError(read.sourceError, { persist: true });
  };
  /** The dry-run timestamp we are waiting to move; null when none is in flight. */
  const [awaitedDryRunAt, setAwaitedDryRunAt] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const preflightRun = useRunPreflight(jobId ?? '');
  const preflightClear = useClearPreflight(jobId ?? '');
  const creativesUploading = useCreativesUploading(jobId ?? '');

  /**
   * A real server-side re-check: it reruns the rule set and rewrites what each node
   * carries, so the sheet redraws from the response rather than from what the parse
   * happened to store.
   */
  const runPreflight = () => {
    setLocalPreflight('running');
    preflightRun.mutate(undefined, {
      onSuccess: (result) => {
        setLocalPreflight('checked');

        // Said whether or not the plan passed, and before the early return below, because
        // this is the case where it matters most: a clean verdict on a platform whose
        // existing objects could not be read has not established that any of these names
        // is free. Publishing then makes a second copy of something already there, and
        // nothing in the findings would have hinted at it.
        if (result.uncheckedPlatforms.length > 0) {
          notificationService.showWarning(
            t('projectDetail.jobReview.namesNotCheckedOn', {
              platforms: result.uncheckedPlatforms
                .map(platformLabel)
                .join(t('projectDetail.jobReview.accountJoin')),
            }),
          );
        }

        // One action, both checks. The platform half still waits for our own to
        // pass: an object missing a required field is rejected for that, which
        // tells the reviewer nothing they did not already know and spends a call
        // against the platform's rate limit to say it.
        if (result.canPublish) {
          startDryRun();
          return;
        }

        // Otherwise say what happened. A run that finds nothing and starts no
        // platform check is over in a blink and used to leave no trace at all,
        // which reads as a button that does nothing — and the most common reason
        // for stopping here is an account nothing can reach, which is worth
        // naming rather than leaving to be inferred from a disabled button.
        if (result.unusableAccounts.length > 0) {
          notificationService.showError(
            t('projectDetail.jobReview.checkedThePlanBut', {
              accounts: result.unusableAccounts
                .map(describeUnusableAccount)
                .join(t('projectDetail.jobReview.accountJoin')),
            }),
          );
          return;
        }
        notificationService.showSuccess(summarisePreflight(result));
      },
      onError: (error) => {
        // Back to idle, not 'checked': a failed check must not read as a pass.
        setLocalPreflight('idle');
        notificationService.showError(
          error instanceof Error
            ? error.message
            : t('projectDetail.jobReview.couldNotRunThePreflightCheck'),
        );
      },
    });
  };

  const { data: job, isLoading } = useQuery(
    deliveryJobQueryOptions(jobId ?? '', projectId, awaitedDryRunAt !== null),
  );

  /**
   * The server decides where a freshly loaded job starts; a local transition takes
   * over from the moment the reviewer does something. Without this a reload showed
   * `idle` and hid findings that were still stored, which reads as losing them.
   */
  const preflight: PreflightStatus = localPreflight ?? statusFromJob(job?.preflight);

  const clearPreflight = () => {
    preflightClear.mutate(undefined, {
      onSuccess: () => {
        setLocalPreflight('idle');
        notificationService.showSuccess(t('projectDetail.jobReview.findingsCleared'));
      },
      onError: (error) =>
        notificationService.showError(
          error instanceof Error
            ? error.message
            : t('projectDetail.jobReview.couldNotClearTheFindings'),
        ),
    });
  };
  const { data: plan, refetch: refetchPlan } = useQuery(jobPlanQueryOptions(jobId ?? ''));
  // Every column, label and required marker comes from the platform's schema, so the
  // table cannot be drawn before it arrives.
  const schemas = usePlatformSchemas();

  const editField = useEditNodeField(jobId ?? '');
  const bindAccount = useBindJobAccount(jobId ?? '');
  const removePlatform = useRemoveJobPlatform(jobId ?? '');
  const bindPage = useBindJobPage(jobId ?? '');
  const bindAdvertiser = useBindJobAdvertiser(jobId ?? '');
  const publish = usePublishJob(jobId ?? '');
  const checkDelivery = useCheckDeliveryStatus(jobId ?? '');
  const fetchReport = useFetchPublishReport(jobId ?? '');
  const dryRun = useDryRunJob(jobId ?? '');

  const isPublishing = job?.status === 'publishing';

  /**
   * A dry run leaves the job status alone — a check does not change what the plan
   * is — so the only sign it landed is its recorded timestamp moving. `NEVER_RUN`
   * stands in for a job that has never been checked, so the first run has something
   * to differ from.
   */
  const dryRunAt = job?.dryRun?.lastRunAt ?? NEVER_RUN;
  const isDryRunning = dryRun.isPending || awaitedDryRunAt !== null;

  const startDryRun = () => {
    setAwaitedDryRunAt(dryRunAt);
    dryRun.mutate(undefined, {
      onError: (error: unknown) => {
        setAwaitedDryRunAt(null);
        notificationService.showError(
          error instanceof Error
            ? error.message
            : t('projectDetail.jobReview.couldNotCheckWithThePlatform'),
        );
      },
    });
  };

  useEffect(() => {
    if (awaitedDryRunAt === null || dryRunAt === awaitedDryRunAt) return;
    setAwaitedDryRunAt(null);

    // The objections are written onto the rows, so the sheet has to be pulled again.
    void queryClient.invalidateQueries({
      queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.jobPlan(jobId ?? ''),
    });
    // The status is deliberately left alone. A check is not an edit, and marking it
    // stale told the operator the plan had changed since the last run when nothing
    // had. Any objection the run raised is an error on a row, which `planBlocker`
    // already refuses to publish past.
  }, [dryRunAt, awaitedDryRunAt, jobId, queryClient]);

  /**
   * The worker records the run only inside its success path, so a throw against a
   * live platform API means the timestamp never moves and nothing else would ever
   * clear the wait — leaving the tab polling every few seconds with the button
   * stuck disabled until it is reloaded.
   */
  useEffect(() => {
    if (awaitedDryRunAt === null) return;
    const timer = setTimeout(() => {
      setAwaitedDryRunAt(null);
      notificationService.showError(t('projectDetail.jobReview.platformCheckDidNotReportBack'));
    }, DRY_RUN_WAIT_LIMIT_MS);
    return () => clearTimeout(timer);
  }, [awaitedDryRunAt, t]);

  /**
   * The run finished on a worker, so the report is collected when the status
   * settles rather than returned by the call that started it.
   *
   * Keyed on the transition out of `publishing`, so a reviewer opening a job that
   * finished while they were elsewhere is not shown a dialog about a run they did
   * not start.
   */
  const wasPublishing = useRef(false);
  useEffect(() => {
    if (isPublishing) {
      wasPublishing.current = true;
      return;
    }
    if (!wasPublishing.current) return;
    wasPublishing.current = false;

    fetchReport.mutate(undefined, {
      onSuccess: setReport,
      onError: (error) =>
        notificationService.showError(
          error instanceof Error
            ? error.message
            : t('projectDetail.jobReview.couldNotReadWhatThePublishDid'),
        ),
    });
    // `fetchReport` is a fresh object each render; depending on it would re-run this
    // on every render rather than on the transition it is watching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublishing]);

  // A reload part way through a zip has no mutation left to refetch the plan once it lands.
  const isReadingSource = job?.isReadingSource ?? false;
  const wasReadingSource = useRef(false);
  useEffect(() => {
    if (isReadingSource) {
      wasReadingSource.current = true;
      return;
    }
    if (!wasReadingSource.current) return;
    wasReadingSource.current = false;
    void refetchPlan();
  }, [isReadingSource, refetchPlan]);

  const backToDelivery = () => void navigate(ROUTES.PROJECT_DELIVERY(projectId));

  // The way forward from any state with no plan in it: the same upload the new-job flow uses.
  const uploadAction = (
    <>
      <input
        ref={uploadInput}
        type="file"
        accept=".xlsx,.csv,.zip"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) uploadPlan(file);
        }}
        data-testid="job-upload-input"
      />
      <Button
        size="default"
        onClick={() => uploadInput.current?.click()}
        disabled={uploadSource.isPending}
        data-testid="job-upload-plan"
      >
        {uploadSource.isPending
          ? t('projectDetail.jobReview.uploadingAPlan')
          : t('projectDetail.jobReview.uploadAPlan')}
      </Button>
    </>
  );

  if (isLoading || !plan || !schemas.isReady) {
    return (
      <div className="flex flex-1 items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  // Resolved against the plan rather than held as the source of truth, so a tab
  // whose platform disappears from a re-parse falls back instead of blanking.
  const active = plan.groups.find((group) => group.platform === platform) ?? plan.groups[0];

  if (isReadingSource) {
    const progress = job?.parseProgress;
    const detail = progress
      ? t('projectDetail.jobReview.readingProgress', {
          step: progress.step,
          tabsDone: progress.tabsDone,
          tabsTotal: progress.tabsTotal,
          rowsDone: progress.rowsDone,
          rowsTotal: progress.rowsTotal,
        })
      : job?.source.kind === 'zip'
        ? t('projectDetail.jobReview.readingThePlanDetail')
        : t('projectDetail.jobReview.readingThePlanDetailAgent');
    return (
      <EmptyState
        icon={<Spinner />}
        title={t('projectDetail.jobReview.readingThePlan')}
        description={detail}
        data-testid="job-reading-source"
      />
    );
  }

  if (job?.pendingSource && plan.groups.every((group) => group.nodes.length === 0)) {
    const pending = job.pendingSource;
    return (
      <>
        <EmptyState
          title={t('projectDetail.jobReview.tabsToChoose')}
          description={t('projectDetail.jobReview.tabsToChooseDetail')}
          data-testid="job-pending-source"
        >
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="default"
              onClick={() => dismissPending.mutate(job.id)}
              disabled={dismissPending.isPending}
            >
              {t('projectDetail.jobReview.dropUpload')}
            </Button>
            <Button
              size="default"
              onClick={() => setIsTabPickerOpen(true)}
              data-testid="job-choose-tabs"
            >
              {t('projectDetail.jobReview.chooseTabs')}
            </Button>
          </div>
        </EmptyState>
        {isTabPickerOpen && (
          <TabPickerDialog
            open
            tabs={pending.sheets}
            isSubmitting={readTabs.isPending}
            onConfirm={(sheets) =>
              readTabs.mutate(
                { jobId: job.id, sheets },
                {
                  onSuccess: ({ ingested }) => {
                    setIsTabPickerOpen(false);
                    void ingested.then((read) => {
                      if (read.sourceError) {
                        notificationService.showError(read.sourceError, { persist: true });
                      }
                    });
                  },
                  onError: (error) =>
                    notificationService.showError(
                      error instanceof Error
                        ? error.message
                        : t('projectDetail.jobReview.planCouldNotBeRead'),
                    ),
                },
              )
            }
            onClose={() => setIsTabPickerOpen(false)}
          />
        )}
      </>
    );
  }

  if (job?.sourceError && plan.groups.every((group) => group.nodes.length === 0)) {
    return (
      <EmptyState
        title={t('projectDetail.jobReview.planCouldNotBeRead')}
        description={job.sourceError}
        data-testid="job-source-error"
      >
        {uploadAction}
      </EmptyState>
    );
  }

  if (job && plan.groups.length > 0 && plan.groups.every((group) => group.nodes.length === 0)) {
    return (
      <EmptyState
        title={t('projectDetail.jobReview.noPlanYet')}
        description={t('projectDetail.jobReview.noPlanYetDetail')}
        data-testid="job-no-plan"
      >
        {uploadAction}
      </EmptyState>
    );
  }

  if (!job || plan.groups.length === 0) {
    return (
      <EmptyState
        title={t('projectDetail.jobReview.jobNotFound')}
        description={t('projectDetail.jobReview.itMayHaveBeenRemoved')}
        data-testid="job-not-found"
      />
    );
  }

  const gate = publishGate(plan, preflight, job.status === 'published');

  return (
    <TooltipProvider>
      <ListPageLayout data-testid="job-review-page">
        <PageTransition variant="slideUp" delay={0}>
          {/* Everything above the sheet is fixed height so the sheet takes the
              rest: its scrollbars belong at the bottom of the screen, not tucked
              under the last row when a plan only has three of them. */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border-light bg-card px-4 py-3">
            {/* No flex-1 here. A zero basis would make this group "fit" any width, so the row
                would never wrap and the group would instead spill over the actions; sized by
                its content, it wraps them onto a second row when the space is genuinely gone. */}
            <div className="flex min-w-0 items-center gap-2">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-surface text-foreground dark:bg-surface-raised">
                <PackageIcon className="size-5" aria-hidden />
              </div>
              <button
                type="button"
                onClick={backToDelivery}
                className="shrink-0 text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
                data-testid="job-review-back"
              >
                {t('projectDetail.jobReview.delivery')}
              </button>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              {/* Capped rather than left to its content: an untitled-length job name would
                  widen this group enough to wrap the actions off the row on its own. Bounding
                  it here keeps the group's width predictable and truncates the tail instead. */}
              <span className="max-w-[18rem] truncate text-sm font-semibold text-foreground">
                {job.title}
              </span>
            </div>

            <PublishActions
              gate={gate}
              status={preflight}
              isPublishing={publish.isPending || isPublishing}
              onRunPreflight={runPreflight}
              onClearPreflight={clearPreflight}
              isClearing={preflightClear.isPending}
              onOpenHistory={() => setIsHistoryOpen(true)}
              isDryRunning={isDryRunning}
              creativesUploading={creativesUploading}
              onPublish={() => setIsPublishConfirmOpen(true)}
            />
          </div>
        </PageTransition>

        <JobPlatformTabs
          groups={plan.groups}
          value={active.platform}
          onChange={setPlatform}
          showIssues={showsIssues(preflight)}
          onRemove={(platform) => {
            // Move off the tab first: it is about to stop existing, and a table
            // rendering a group that has gone is a blank screen.
            const next = plan.groups.find((group) => group.platform !== platform);
            if (next) setPlatform(next.platform);
            removePlatform.mutate(platform, {
              onError: (error) =>
                notificationService.showError(
                  error instanceof Error
                    ? error.message
                    : t('projectDetail.jobReview.couldNotRemoveThisPlatform'),
                ),
            });
          }}
          removing={
            removePlatform.isPending ? (removePlatform.variables as ConnectorPlatform) : null
          }
        />

        <PageTransition variant="slideUp" delay={0.05} className="flex min-h-0 flex-1 flex-col">
          <JobPlatformPanel
            jobId={jobId ?? ''}
            key={active.platform}
            group={active}
            onEditField={(nodeId, key, value) => {
              // Both handlers move the gate to stale before the request lands, so a
              // rejection has to put it back: otherwise Publish stays locked, the cell
              // redraws its old value, and nothing says the edit never happened.
              const before = preflight;
              setLocalPreflight(afterEdit(preflight));
              editField.mutate(
                { nodeId, key, value },
                {
                  onError: (error: unknown) => {
                    setLocalPreflight(before);
                    notificationService.showError(
                      error instanceof Error
                        ? error.message
                        : t('projectDetail.jobReview.couldNotSaveThatChange'),
                    );
                  },
                },
              );
            }}
            onBindPage={(pageId, pageName) => {
              const before = preflight;
              setLocalPreflight(afterEdit(preflight));
              bindPage.mutate(
                { platform: active.platform, pageId, pageName },
                {
                  onError: (error: unknown) => {
                    setLocalPreflight(before);
                    notificationService.showError(
                      error instanceof Error
                        ? error.message
                        : t('projectDetail.jobReview.couldNotSelectThatPage'),
                    );
                  },
                },
              );
            }}
            onBindAdvertiser={(advertiserId) => {
              const before = preflight;
              setLocalPreflight(afterEdit(preflight));
              bindAdvertiser.mutate(
                { platform: active.platform, advertiserId },
                {
                  onError: (error: unknown) => {
                    setLocalPreflight(before);
                    // The server says why: a client it cannot reach, or one under another account.
                    notificationService.showError(
                      error instanceof Error
                        ? error.message
                        : t('projectDetail.jobReview.couldNotSelectThatClient'),
                    );
                  },
                },
              );
            }}
            onBindAccount={(connectorId, accountName) => {
              const before = preflight;
              setLocalPreflight(afterEdit(preflight));
              bindAccount.mutate(
                { platform: active.platform, connectorId, accountName },
                {
                  onError: (error: unknown) => {
                    setLocalPreflight(before);
                    notificationService.showError(
                      error instanceof Error
                        ? error.message
                        : t('projectDetail.jobReview.couldNotBindThatAdAccount'),
                    );
                  },
                },
              );
            }}
            showIssues={showsIssues(preflight)}
            preflightStatus={preflight}
          />
        </PageTransition>

        <JobHistoryPanel
          open={isHistoryOpen}
          jobId={jobId ?? ''}
          onClose={() => setIsHistoryOpen(false)}
        />

        <PublishConfirmDialog
          open={isPublishConfirmOpen}
          groups={plan.groups}
          isPublishing={publish.isPending}
          onCancel={() => setIsPublishConfirmOpen(false)}
          onConfirm={() =>
            publish.mutate(undefined, {
              onSuccess: () => setIsPublishConfirmOpen(false),
              onError: (error) => {
                setIsPublishConfirmOpen(false);
                notificationService.showError(
                  error instanceof Error
                    ? error.message
                    : t('projectDetail.jobReview.couldNotPublishThisJob'),
                );
              },
            })
          }
        />

        <PublishReportDialog
          report={report}
          // Offered only once something is out there: on a run that published nothing
          // there is no object to ask any platform about.
          onCheckDelivery={
            report && report.results.some((result) => result.outcome !== 'skipped')
              ? () => checkDelivery.mutate(undefined, { onSuccess: setReport })
              : undefined
          }
          isCheckingDelivery={checkDelivery.isPending}
          onClose={() => setReport(null)}
        />
      </ListPageLayout>
    </TooltipProvider>
  );
}
